import React, {
  cloneElement,
  CSSProperties,
  forwardRef,
  FunctionComponent,
  useEffect
} from 'react';
import { createPortal } from 'react-dom';
import { useBarcode } from 'react-barcodes';

import { DoobalHeroShippingLabelResponse } from '@/apis/pharmacy/interface';

interface Props {
  data: DoobalHeroShippingLabelResponse;
  closeDoobalHeroShippingLabelHandle: () => void;
}

interface DefaultComponentProps {
  style?: CSSProperties;
}

const PrintDoobalHeroShippingLabel = ({
  data: {
    bookId,
    deliveryMemo,
    dongGroup,
    orderId,
    receiverLocation,
    senderLocation
  },
  closeDoobalHeroShippingLabelHandle
}: Props) => {
  const containerElement = document.createElement('div');

  const { inputRef: barcodeRef } = useBarcode({
    value: bookId,
    options: {
      displayValue: true,
      background: 'transparent',
      width: 2,
      height: 38
    }
  });

  useEffect(() => {
    const externalWindow: WindowProxy | null = window.open(
      '',
      '',
      'width=1200,height=1200,left=200,top=200'
    );
    if (externalWindow) {
      const css = `@media 100mm 150mm {
          @page {size: 100mm 150mm; margin: 0; padding: 0;}
        }
        
        html, body {margin: 0; padding: 0; display: grid;}
          .label {width: 100mm; height: 150mm;}
          .label:not(:first-of-type) { margin-top: 3mm !important; }`;
      const head =
        externalWindow.document.head ||
        document.getElementsByTagName('head')[0];
      const style = externalWindow.document.createElement('style');
      style.type = 'text/css';
      style.appendChild(document.createTextNode(css));
      head.appendChild(style);

      externalWindow.document.body.appendChild(containerElement);
      externalWindow.onafterprint = () => {
        setTimeout(() => {
          closeDoobalHeroShippingLabelHandle();
          externalWindow.close();
        }, 200);
      };

      externalWindow.print();
    }
  }, []);

  return (
    <>
      {createPortal(
        cloneElement(
          <Label>
            <SenderName>
              {senderLocation.name} T.{senderLocation.phone}
            </SenderName>
            <SenderAddress>
              {senderLocation.address} {senderLocation.addressDetail}
            </SenderAddress>
            <ReceiverAddress>
              {receiverLocation.address} {receiverLocation.addressDetail}
            </ReceiverAddress>
            <ReceiverShippingRequest>{deliveryMemo}</ReceiverShippingRequest>
            <ReceiverName>&lt;{receiverLocation.name}님&gt;</ReceiverName>
            <ReceiverPhone>T: {receiverLocation.phone}</ReceiverPhone>
            <Barcode ref={barcodeRef} />
            <DongGroupName>{dongGroup}</DongGroupName>
            <OrderNumber>주문번호 {orderId}</OrderNumber>
            <ProductInfo>처방약 [1개]</ProductInfo>
          </Label>,
          {}
        ),
        containerElement
      )}
    </>
  );
};

export default PrintDoobalHeroShippingLabel;

const Label: FunctionComponent = ({ children }) => (
  <div
    className="label"
    style={{
      position: 'relative',
      width: '100mm',
      height: '150mm',
      margin: 0,
      padding: 0
    }}>
    {children}
  </div>
);

const DefaultComponent: FunctionComponent<DefaultComponentProps> = ({
  children,
  style = {}
}) => {
  const defaultStyle = {
    transform: 'rotate(-90deg)',
    transformOrigin: 'left top',
    position: 'absolute',
    margin: '0',
    fontSize: '10px',
    whiteSpace: 'pre-wrap',
    lineHeight: '3mm'
  } as CSSProperties;

  return <div style={{ ...defaultStyle, ...style }}>{children}</div>;
};

const SenderName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '16mm',
      width: '51mm',
      fontSize: '14px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
const SenderAddress: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '20mm',
      width: '56mm',
      fontSize: '11px'
    }}>
    {children}
  </DefaultComponent>
);
const ReceiverAddress: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '28mm',
      width: '57mm',
      fontSize: '16px',
      fontWeight: 'bold',
      lineHeight: '4.5mm'
    }}>
    {children}
  </DefaultComponent>
);
const ReceiverShippingRequest: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '46mm',
      width: '57mm',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '4mm'
    }}>
    {children}
  </DefaultComponent>
);
const ReceiverName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '72mm',
      width: '57mm',
      fontSize: '14px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
const ReceiverPhone: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '146mm',
      left: '77mm',
      width: '57mm',
      fontSize: '14px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
const Barcode = forwardRef<HTMLCanvasElement>((props, ref) => (
  <DefaultComponent
    style={{
      top: '150mm',
      left: '80mm'
    }}>
    <canvas ref={ref} {...props} />
  </DefaultComponent>
));
const DongGroupName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '86mm',
      left: '13mm',
      width: '81mm',
      fontSize: '20px',
      fontWeight: 'bold',
      whiteSpace: 'pre'
    }}>
    {children}
  </DefaultComponent>
);
const OrderNumber: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '86mm',
      left: '28mm',
      width: '81mm',
      fontSize: '10px'
    }}>
    {children}
  </DefaultComponent>
);
const ProductInfo: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '86mm',
      left: '32mm',
      width: '81mm',
      fontSize: '12px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
